import React, { memo, useState } from 'react';

import { Badge, Popover, SpaceBetween, StatusIndicator } from '@amzn/awsui-components-react-v3';

import clsx from 'clsx';
import { uniqueId } from 'lodash';

import { AsyncState, Serial } from '../../store/types';
import { ErrorMessages } from './SerialErrors';
import { SerialNumber } from './SerialNumber';
import { SERIAL_ERROR_CODES } from './constants';

import './scanSerials.scss';

interface SerialBaseProps {
  token: Serial;
  disabled: boolean;
}

export interface SerialTokenProps extends SerialBaseProps {
  serials: Serial[];
  containerId: string;
  receiveState: AsyncState;
  onClick?: (s: Serial) => void;
  onsiteSpecificSerialScanned?: (s: Serial, val: string) => void;
}

const COLOR_MAP: any = {
  [AsyncState.ERROR]: 'red',
  [AsyncState.OK]: 'green',
  [AsyncState.CREATED]: 'blue',
  [AsyncState.WARNING]: 'blue',
  [AsyncState.IN_PROGRESS]: 'grey',
};

interface SerialLoadingProps extends SerialBaseProps {
  loading: boolean;
  show: boolean;
  serialNumber?: string;
}

interface SerialWithPopoverProps extends Omit<SerialTokenProps, 'containerId'>, SerialLoadingProps {
  tokenState: AsyncState;
  errorCode: string;
}

export const SerialLoading = ({ loading, token, disabled, show, serialNumber }: SerialLoadingProps) => {
  if (!show) {
    return null;
  }

  return (
    <div>
      {loading ? (
        <StatusIndicator type="loading">
          <span aria-label={`validating serialId ${serialNumber}`}>{serialNumber}</span>
        </StatusIndicator>
      ) : (
        <SerialNumber serial={token} disabled={disabled} />
      )}
    </div>
  );
};

export const SerialWithPopover = ({
  errorCode,
  tokenState,
  token,
  onsiteSpecificSerialScanned,
  onClick,
  show,
}: SerialWithPopoverProps) => {
  const [key, setKey] = useState(uniqueId());

  if (!show) {
    return null;
  }

  return (
    <div>
      <Popover
        key={key}
        dismissButton={true}
        position="top"
        size="large"
        triggerType="custom"
        header={
          <StatusIndicator type={tokenState === AsyncState.ERROR ? 'error' : 'warning'}>
            Serial Validation
          </StatusIndicator>
        }
        content={
          <ErrorMessages
            code={errorCode}
            serial={token}
            onRescan={onsiteSpecificSerialScanned}
            onCancel={() => setKey(uniqueId())}
          />
        }
      >
        <div
          className={clsx('error-text', { warning: token?.state === AsyncState.WARNING })}
          onClick={() => onClick && onClick(token)}
        >
          <SpaceBetween direction="horizontal" size="s">
            <StatusIndicator type={tokenState === AsyncState.ERROR ? 'error' : 'warning'}>
              <SerialNumber serial={token} disabled={false} link={false} />
            </StatusIndicator>
          </SpaceBetween>
        </div>
      </Popover>
    </div>
  );
};

export const SerialToken = memo(
  ({
    token,
    serials = [],
    receiveState,
    disabled = false,
    onClick,
    onsiteSpecificSerialScanned,
  }: SerialTokenProps) => {
    const index = serials.findIndex((s: Serial) => s.id === token?.id);
    const statusCode = token?.state !== AsyncState.OK ? token?.status?.statusCode : null;
    const receievStatusCode =
      token.receiveStatus?.receiveStatusType === AsyncState.ERROR
        ? token.receiveStatus?.statusCode || SERIAL_ERROR_CODES.SERIAL_UNEXPECTED_ERROR
        : null;
    const isReceived = !!receiveState && receiveState !== AsyncState.IN_PROGRESS;
    const error: string | null | undefined = isReceived ? receievStatusCode : statusCode;

    const serialNumber = token?.siteSpecificSerialId || token?.serialNumber;
    const loading = token?.loading || token.receiveStatus?.receiveStatusType === AsyncState.IN_PROGRESS;
    const tokenState = disabled ? token.receiveStatus?.receiveStatusType : token?.state;
    const validationCode = token?.status?.statusCode;
    const errorCode = disabled ? receievStatusCode : validationCode;

    const ariaLabel = `${token?.serialNumber}-status-${tokenState}`;

    return (
      <div className={clsx('serial-id')} aria-label={ariaLabel} role="gridcell">
        <SpaceBetween direction="horizontal" size="s">
          <Badge color={COLOR_MAP[tokenState || AsyncState.CREATED]}>{index + 1}</Badge>
          <SerialLoading
            loading={loading}
            token={token}
            disabled={disabled}
            show={!error}
            serialNumber={serialNumber}
          />
          <SerialWithPopover
            tokenState={tokenState || AsyncState.CREATED}
            errorCode={errorCode || SERIAL_ERROR_CODES.SERIAL_UNEXPECTED_ERROR}
            serials={serials}
            receiveState={receiveState}
            token={token}
            disabled={disabled}
            loading={loading}
            show={!!error}
            onsiteSpecificSerialScanned={onsiteSpecificSerialScanned}
            onClick={onClick}
          />
        </SpaceBetween>
      </div>
    );
  },
);

export default SerialToken;
